import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Box, CircularProgress } from '@material-ui/core';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { get } from '../../helpers/fetchServicesMethods';
import searchResSequence from '../../services/searchResSequence';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        maxWidth: '100%',
        '& a': {
            color: '#337cb7',
            textDecoration: 'none',
            '&:hover': {
                opacity: 0.8
            }
        }
    },
    pageTitle: {
        marginBottom: theme.spacing(3),
        color: '#2F5597',
        fontWeight: 'bold',
        fontSize: '24px',
        borderBottom: '2px solid #337cb7',
        paddingBottom: theme.spacing(1)
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        flexDirection: 'column',
        gap: theme.spacing(2)
    },
    htmlContent: {
        '& .summary-box': {
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            '&:hover': {
                opacity: 0.8
            }
        },
        '& td a:hover': {
            opacity: 0.8
        }
    },
    htmlContentDisabled: {
        '& a': {
            color: '#bbb !important',
            pointerEvents: 'none',
            cursor: 'not-allowed',
            textDecoration: 'none'
        },
        '& .summary-box': {
            cursor: 'not-allowed',
            pointerEvents: 'none',
            opacity: 0.5
        },
        '& td a': {
            color: '#bbb !important',
            pointerEvents: 'none'
        },
        '& h2, & h3, & h4, & h5': {
            cursor: 'default !important',
            color: '#bbb !important',
            textDecoration: 'none !important',
            pointerEvents: 'none'
        }
    },
    cmBanner: {
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '4px',
        padding: '10px 16px',
        marginBottom: theme.spacing(2),
        color: '#856404',
        fontStyle: 'italic'
    }
}));

const AlertReport = () => {
    const classes = useStyles();
    const { workflowId } = useParams();
    const history = useHistory();
    const userInfo = useSelector(state => state.setUserInfo);
    const hasCmAccess = userInfo && userInfo.current_user && userInfo.current_user.cmAccess;
    const [isCmSearch, setIsCmSearch] = useState(false);
    const cmDisabled = isCmSearch && !hasCmAccess;
    const [alertHtml, setAlertHtml] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageTitle, setPageTitle] = useState('Alert Report');

    useEffect(() => {
        const fetchAlertReport = async () => {
            if (!workflowId) {
                setError('No workflow ID provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                // Fetch workflow summary to determine search type
                const summaryResponse = await searchResSequence.getSequenceSummary(workflowId);
                if (summaryResponse && summaryResponse.response_status === 0 && summaryResponse.response_content) {
                    setIsCmSearch(summaryResponse.response_content.type === 'GqWfCmSearch');
                }

                const apiUrl = `do=gqworkflow.get_alert_html&workflow=id:${workflowId}`;
                const response = await get(apiUrl, history);

                if (!response) {
                    throw new Error('Failed to generate alert report - no response from server');
                }

                if (typeof response === 'string') {
                    const htmlContent = response;

                    if (htmlContent.includes('Alert email not found') || htmlContent.includes('not found')) {
                        setError('Alert has not been created for this search');
                        setLoading(false);
                        return;
                    }

                    // Extract title from HTML if available
                    // Try to extract from hidden div first (browser view)
                    const titleDivMatch = htmlContent.match(/<div id="alert-workflow-title"[^>]*>([\s\S]*?)<\/div>/);
                    let searchName = '';
                    
                    if (titleDivMatch && titleDivMatch[1]) {
                        // Remove HTML tags and decode entities
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = titleDivMatch[1];
                        searchName = tempDiv.textContent || tempDiv.innerText || '';
                        searchName = searchName.trim();
                        console.log('Extracted from div:', searchName);
                    } else {
                        //Fallback: try to extract from email greeting text
                        const titleMatch = htmlContent.match(/Your IP workflow run "<b>([^<]+)<\/b>"/);
                        if (titleMatch && titleMatch[1]) {
                            searchName = titleMatch[1].trim();
                            console.log('Extracted from email:', searchName);
                        }
                    }
                    
                    const fullTitle = searchName ? `Alert Report - ${searchName}` : `Alert Report - ${workflowId}`;
                    setPageTitle(fullTitle);
                    document.title = fullTitle; // Update browser tab title
                    console.log('Final title:', fullTitle);

                    setAlertHtml(htmlContent);
                    setError(null);
                } else {
                    throw new Error('Unexpected response format');
                }
            } catch (err) {
                console.error('Error fetching alert report:', err);
                setError(err.message || 'Failed to load alert report');
                toast.error(err.message || 'Failed to load alert report');
            } finally {
                setLoading(false);
            }
        };

        fetchAlertReport();
    }, [workflowId, history]);

    // Execute embedded scripts and add scroll functionality
    useEffect(() => {
        if (alertHtml) {
            // Extract and execute script tags from the HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = alertHtml;
            const scripts = tempDiv.querySelectorAll('script');

            const addedScripts = [];
            scripts.forEach(script => {
                try {
                    if (script.src) {
                        // External script
                        const newScript = document.createElement('script');
                        newScript.src = script.src;
                        newScript.async = false;
                        document.head.appendChild(newScript);
                        addedScripts.push(newScript);
                    } else {
                        // Inline script - execute directly in global scope using eval
                        const scriptContent = script.textContent;
                        // eslint-disable-next-line no-eval
                        window.eval(scriptContent);
                    }
                } catch (error) {
                    console.error('Error executing script:', error);
                }
            });

            // Verify functions are available
            console.log('scrollToTable available:', typeof window.scrollToTable);
            console.log('downloadExcel available:', typeof window.downloadExcel);

            // Debug: Log all table IDs on the page
            const tables = document.querySelectorAll('[id^="table-"]');
            console.log('Found table IDs:', Array.from(tables).map(t => t.id));

            // Debug: Log all tile onclick attributes
            const tiles = document.querySelectorAll('a[onclick*="scrollToTable"]');
            console.log('Found tiles with onclick:', Array.from(tiles).map(t => ({
                text: t.textContent.trim(),
                onclick: t.getAttribute('onclick')
            })));

            // Add click handlers to table headers to navigate to Discover with filters
            const findAndEnhanceTableHeaders = () => {
                // Skip enhancing headers if CM search without access
                if (cmDisabled) return;

                // Find all tables with IDs
                const allTables = document.querySelectorAll('table[id^="table-"]');
                console.log('Found tables to enhance:', Array.from(allTables).map(t => t.id));
                
                allTables.forEach(table => {
                    // Find the heading above each table (h2, h3, h4, h5)
                    let heading = table.previousElementSibling;
                    while (heading && !['H2', 'H3', 'H4', 'H5'].includes(heading.tagName)) {
                        heading = heading.previousElementSibling;
                    }
                    
                    if (heading) {
                        const headingText = heading.textContent.toLowerCase();
                        let alertType = null;
                        
                        // Determine alert type based on heading text
                        if (headingText.includes('new results') || headingText.includes('all results')) {
                            alertType = 'newhits';
                        } else if (headingText.includes('legal status') && !headingText.includes('extended')) {
                            alertType = 'legal';
                        } else if (headingText.includes('extended')) {
                            alertType = 'extended';
                        } else if (headingText.includes('family')) {
                            alertType = 'family';
                        }
                        
                        if (alertType) {
                            // Make heading clickable and style it
                            heading.style.cursor = 'pointer';
                            heading.style.color = '#337ab7';
                            heading.style.textDecoration = 'underline';
                            heading.title = 'Click to view these patents in Discover';
                            
                            heading.addEventListener('click', (e) => {
                                e.preventDefault();
                                // Navigate to Discover with alert filter
                                const resid = `res${workflowId}.resdb`;
                                console.log('Navigating to Discover with filter:', alertType);
                                history.push(`/discover/${resid}/${alertType}`);
                            });
                            
                            console.log('Enhanced heading:', heading.textContent, 'with alert type:', alertType);
                        }
                    }
                });
            };
            
            // Run after a short delay to ensure DOM is ready
            setTimeout(findAndEnhanceTableHeaders, 100);

            // Cleanup on unmount
            return () => {
                // Remove added external scripts
                addedScripts.forEach(script => {
                    if (script.parentNode) {
                        script.parentNode.removeChild(script);
                    }
                });
                // Clean up window functions
                delete window.scrollToTable;
                delete window.downloadExcel;
            };
        }
    }, [alertHtml, history, workflowId, cmDisabled]);

    if (loading) {
        return (
            <Box className={classes.loading}>
                <CircularProgress />
                <Typography>Loading alert report...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box className={classes.root}>
                <Typography color="error" variant="h6">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box className={classes.root}>
            {cmDisabled && alertHtml && (
                <div className={classes.cmBanner}>
                    Access to these results requires an active subscription to the Chemical Modification Module. All links are disabled.
                </div>
            )}
            {alertHtml && (
                <div
                    className={cmDisabled ? `${classes.htmlContent} ${classes.htmlContentDisabled}` : classes.htmlContent}
                    dangerouslySetInnerHTML={{ __html: alertHtml }}
                />
            )}
        </Box>
    );
};

export default AlertReport;
